import { useTranslation } from 'react-i18next';
import Button from '~/components/buttons/NewButton';
import SimpleTooltip from '~/components/data-display/SimpleTooltip';
import Text from '~/components/typographies/Text';
import IconAttach from '~/icons/icon-attach.svg?react';
import IconDownload from '~/icons/icon-download.svg?react';
import { Transaction } from '~/types/transaction';
import { TransactionAttachment as Attachment } from '~/types/transaction-attachment';
import { openUrl } from '~/utils/download';
import styles from './BookkeepingTransactionAttachments.module.scss';

interface Props {
  attachments: Transaction['attachments'];
  notes: Transaction['notes'];
}

const TransactionAttachment = ({ attachment }: { attachment: Attachment }) => {
  const { t } = useTranslation('transactions');

  return (
    <Button
      variant="outlined"
      onClick={() => openUrl(attachment.path)}
      className={styles.attachment}
      aria-label={t('aside.download')}
    >
      <IconAttach className={styles.attachmentIcon} />

      <Text variant="body2" weight="semibold" ellipsis>
        {attachment.filename}
      </Text>

      <IconDownload className={styles.downloadIcon} />
    </Button>
  );
};

const BookkeepingTransactionAttachments = ({ attachments, notes }: Props) => {
  const { t } = useTranslation('transactions');
  const hasAttachments = attachments && attachments.length > 0;
  const hasNotes = notes && notes.length > 0;

  return (
    <dl className={styles.container}>
      {hasNotes && (
        <>
          <dt className={styles.notesTitle}>
            <Text variant="caption" weight="semibold">
              {t('aside.added_notes')}:
            </Text>
          </dt>
          <dd>
            <SimpleTooltip title={notes} placement="top left" mode="div" tooltipClassName={styles.tooltip}>
              {notes.length > 290 ? notes.slice(0, 290).concat('...') : notes}
            </SimpleTooltip>
          </dd>
        </>
      )}
      {hasAttachments && (
        <>
          <dt>
            <Text variant="caption" weight="semibold">
              {t('aside.attachments')}:
            </Text>
          </dt>
          <dd className={styles.attachments}>
            {attachments.map((a) => (
              <TransactionAttachment key={a.id} attachment={a} />
            ))}
          </dd>
        </>
      )}
    </dl>
  );
};

export default BookkeepingTransactionAttachments;
